export interface IdleTaskOptions {
  /** This value should be set to true when a task should be executed before the current page is exited */
  ensureExecution?: boolean;
}

interface TaskQueueItem {
  /** Set to true if we should try to run this task before the tab closes */
  ensureExecution: boolean;

  /** The task to run */
  task: () => void;
}

// A set of tasks that still have to be run
let taskQueue: TaskQueueItem[] = [];

// Set to true when we have a pending idle task scheduled
let idleTaskScheduled = false;

// Tracks if the handler that runs all ensureExecution tasks before the end of the session
let sessionEndHandlerRegistered = false;

/**
 * A function that schedules a non critical task to be run when the browser has cycles available
 * @param task - The task to be executed
 * @param options - Optional configuration for the task execution
 */
export function scheduleIdleTask(task: () => void, options?: IdleTaskOptions) {
  taskQueue.push({
    ensureExecution: options?.ensureExecution === true,
    task
  });

  // Set up the handler to run before the document is disposed if this is the first ensureExecution task
  if (options?.ensureExecution && !sessionEndHandlerRegistered) {
    // The visibilitychange handler is the last guaranteed event that happens on page navigation: https://developers.google.com/web/updates/2018/07/page-lifecycle-api#advice-hidden
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        runTasksWithEnsureExecution();
      }
    });
    sessionEndHandlerRegistered = true;
  }

  ensureIdleCallback();
}

/**
 * Ensures an idle callback has been scheduled for the remaining tasks
 */
function ensureIdleCallback() {
  if (!idleTaskScheduled) {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(idleTaskCallback);
    } else {
      const deadline = Date.now() + 50;
      window.setTimeout(
        () => idleTaskCallback({ timeRemaining: () => Math.max(deadline - Date.now(), 0), didTimeout: false }),
        0
      );
    }
    idleTaskScheduled = true;
  }
}

/**
 * Runs tasks from the task queue
 * @param filter - An optional function that will be called for each task to see if it should run. Returns false for tasks that should not run. If omitted all tasks run.
 * @param shouldContinueRunning - An optional function that will be called to determine if we have enough time to continue running tasks. If omitted, we don't stop running tasks.
 */
function runTasks(filter?: (taskQueueItem: TaskQueueItem) => boolean, shouldContinueRunning?: () => boolean) {
  // The next value for the task queue
  let newTaskQueue: TaskQueueItem[] = [];

  for (let index = 0; index < taskQueue.length; index += 1) {
    if (shouldContinueRunning && !shouldContinueRunning()) {
      // Add the tasks we didn't get to to the end of the new task queue
      newTaskQueue.push(...taskQueue.slice(index));
      break;
    }

    const taskQueueItem = taskQueue[index];

    if (filter && !filter(taskQueueItem)) {
      newTaskQueue.push(taskQueueItem);
    } else {
      taskQueueItem.task();
    }
  }

  taskQueue = newTaskQueue;
}

// Runs all the tasks in the task queue
function idleTaskCallback(deadline: { timeRemaining: () => number; readonly didTimeout: boolean }) {
  // Minimum time that must be available on deadline to run any more tasks
  const minTaskTime = 10;
  runTasks(undefined, () => deadline.timeRemaining() > minTaskTime);
  idleTaskScheduled = false;

  // If we didn't run through the entire queue, schedule another idle callback
  if (taskQueue.length > 0) {
    ensureIdleCallback();
  }
}

// Runs all tasks scheduled with ensureExecution set
function runTasksWithEnsureExecution() {
  runTasks((task) => task.ensureExecution);
}
